import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { driverSettlementsAPI, type DriverSettlementPayload } from 'api/driverSettlements'
import { isOfflineQueuedFulfilledAction } from 'helpers/offlineAction'
import { branchChanged } from './branchSlice'

/**
 * Generate a stable client-side UUID for idempotency.
 * IMPORTANT: this MUST be called at the dispatcher (component) — not inside the thunk executor —
 * so the UUID reaches `action.meta.arg` and gets captured by the offline middleware's queue.
 * Otherwise, offline replays would lose the UUID and break idempotency.
 */
export function generateClientUuid(): string {
 if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
 return crypto.randomUUID()
 }
 return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
 const r = (Math.random() * 16) | 0
 const v = c === 'x' ? r : (r & 0x3) | 0x8
 return v.toString(16)
 })
}

export interface DriverSettlementRecord {
 id: string
 branch_id: string
 driver_id: string
 shift_id?: string
 vault_id: string
 deliveries_count: number
 total_collected: number
 total_driver_earnings: number
 total_due_from_driver: number
 amount_received: number
 difference: number
 status: string
 notes?: string | null
 settled_at?: string
 created_at?: string
 client_uuid?: string | null
 driver?: { id: string; name: string; phone?: string }
 vault?: { id: string; name: string }
}

interface DriverSettlementsState {
 data: DriverSettlementRecord[]
 loading: boolean
 error: string | null
}

const initialState: DriverSettlementsState = {
 data: [],
 loading: false,
 error: null,
}

export const fetchDriverSettlements = createAsyncThunk(
 'driverSettlements/fetchDriverSettlements',
 async (params: any, { rejectWithValue }) => {
 try {
 const response = await driverSettlementsAPI.list(params)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.driverSettlements.fetchFailed'))
 }
 }
)

export const createSettlement = createAsyncThunk(
 'driverSettlements/createSettlement',
 async (payload: DriverSettlementPayload, { rejectWithValue }) => {
 try {
 // Caller is responsible for setting `payload.client_uuid` (use generateClientUuid()).
 // We don't generate here because the offline middleware captures `action.meta.arg`,
 // not the executor's locals — generating here would break idempotency on replay.
 const response = await driverSettlementsAPI.create(payload)
 return response.data
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.driverSettlements.createFailed'))
 }
 }
)

const driverSettlementsSlice = createSlice({
 name: 'driverSettlements',
 initialState,
 reducers: {},
 extraReducers: (builder) => {
 builder
 .addCase(branchChanged, () => initialState)
 .addCase(fetchDriverSettlements.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchDriverSettlements.fulfilled, (state, action) => {
 state.loading = false
 const payload = action.payload as any
 const list = Array.isArray(payload?.data?.settlements)
 ? payload.data.settlements
 : Array.isArray(payload?.settlements)
 ? payload.settlements
 : Array.isArray(payload?.data)
 ? payload.data
 : []
 state.data = list
 })
 .addCase(fetchDriverSettlements.rejected, (state, action) => {
 state.loading = false
 state.error = (action.payload as string) || i18n.t('errors.driverSettlements.fetchFailed')
 })
 .addCase(createSettlement.fulfilled, (state, action) => {
 if (isOfflineQueuedFulfilledAction(action)) return
 const created = action.payload as any
 const record = created?.data ?? created
 if (record?.id) state.data.unshift(record)
 })
 },
})

export default driverSettlementsSlice.reducer
